/* Dreibach redesign behaviour: quick add, spotlight options, rails, countdown, shop-by hover. */
(() => {
  if (window.__drRedesign) return;
  window.__drRedesign = true;

  const toast = (message) => {
    let el = document.querySelector('.dr-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'dr-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.append(el);
    }
    el.textContent = message;
    el.classList.add('is-on');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-on'), 3200);
  };

  /* ---------- Add to basket without leaving the page ---------- */
  // Load the theme's event classes up front: the request must start synchronously inside the submit
  // handler (like the theme's own product form), otherwise Shopify's analytics reads it as a plain form post.
  const themeEvents = import('@theme/events').catch(() => null);
  // Bound on each form (like the theme's product form), so the default is prevented before
  // document-level listeners such as Shopify's analytics see the submit.
  const onAddSubmit = (event) => {
    const form = event.currentTarget;
    event.preventDefault();
    if (form.classList.contains('is-busy')) return;
    const id = new FormData(form).get('id');
    if (!id) return;
    form.classList.add('is-busy');
    const productId = form.elements['product-id']?.value || '';
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((e) => e.dataset.sectionId))].slice(0, 5);
    const body = new FormData();
    body.append('form_type', 'product');
    body.append('utf8', '✓');
    body.append('id', id);
    body.append('quantity', '1');
    if (productId) body.append('product-id', productId);
    if (sections.length) body.append('sections', sections.join(','));
    fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
      body,
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add this item');
        form.classList.add('is-added');
        setTimeout(() => form.classList.remove('is-added'), 2200);
        const mod = await themeEvents;
        if (mod?.CartAddEvent) {
          form.dispatchEvent(new mod.CartAddEvent({}, 'dr-quick-add', { source: 'dr-quick-add', productId, itemCount: 1, variantId: String(id), sections: data.sections }));
        } else {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        }
      })
      .catch((error) => toast(error.message || 'Sorry, something went wrong. Please try again.'))
      .finally(() => form.classList.remove('is-busy'));
  };
  const initAddForms = (root = document) => {
    root.querySelectorAll('form.dr-add-form').forEach((form) => {
      if (form._dr) return;
      form._dr = true;
      form.addEventListener('submit', onAddSubmit);
    });
  };

  /* ---------- Spotlight: price follows the chosen option ---------- */
  document.addEventListener('change', (event) => {
    const input = event.target.closest('.dr-opt input[type="radio"]');
    if (!input) return;
    const wrap = input.closest('form')?.querySelector('[data-dr-live-price] .dr-price');
    if (!wrap) return;
    const { price, was, off } = input.dataset;
    const sale = Boolean(was) && Number(off) > 0;
    wrap.classList.toggle('dr-price--sale', sale);
    wrap.querySelector('.dr-price__from')?.remove();
    wrap.querySelector('.dr-price__tag').textContent = price;
    wrap.querySelectorAll('.dr-price__was, .dr-price__off').forEach((n) => n.remove());
    if (sale) {
      const s = document.createElement('s');
      s.className = 'dr-price__was';
      s.textContent = was;
      const o = document.createElement('span');
      o.className = 'dr-price__off';
      o.textContent = `-${off}%`;
      wrap.append(s, o);
    }
  });

  /* ---------- Rails: arrow buttons ---------- */
  const initRails = (root = document) => {
    root.querySelectorAll('[data-dr-arrows]').forEach((arrows) => {
      if (arrows._dr) return;
      arrows._dr = true;
      const scope = arrows.closest('.dr-wrap') || arrows.parentElement;
      const list = scope.querySelector('[data-dr-scroller]');
      if (!list) return;
      const [prev, next] = arrows.querySelectorAll('button');
      const update = () => {
        prev.disabled = list.scrollLeft <= 4;
        next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 4;
        arrows.hidden = list.scrollWidth <= list.clientWidth + 4;
      };
      arrows.addEventListener('click', (e) => {
        const b = e.target.closest('button');
        if (!b) return;
        list.scrollBy({ left: Number(b.dataset.dir) * list.clientWidth * 0.9, behavior: 'smooth' });
      });
      list.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      new ResizeObserver(update).observe(list);
      update();
    });
  };

  /* ---------- Days to Christmas (UK date) ---------- */
  const initCountdown = (root = document) => {
    root.querySelectorAll('[data-dr-countdown]').forEach((el) => {
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(new Date());
      const get = (t) => Number(parts.find((p) => p.type === t).value);
      const today = Date.UTC(get('year'), get('month') - 1, get('day'));
      let target = Date.UTC(get('year'), Number(el.dataset.month) - 1, Number(el.dataset.day));
      if (today > target) target = Date.UTC(get('year') + 1, Number(el.dataset.month) - 1, Number(el.dataset.day));
      const days = Math.round((target - today) / 86400000);
      const num = el.querySelector('[data-num]');
      const label = el.querySelector('[data-label-el]');
      if (days === 0) {
        num.textContent = '🎄';
        label.textContent = el.dataset.today;
      } else {
        num.textContent = days;
        label.textContent = days === 1 ? el.dataset.label.replace(/days/i, 'day') : el.dataset.label;
      }
    });
  };

  /* ---------- Shop by: picture follows hover/focus ---------- */
  const initShopBy = (root = document) => {
    root.querySelectorAll('[data-dr-shopby]').forEach((section) => {
      if (section._dr) return;
      section._dr = true;
      const pics = section.querySelectorAll('.dr-shopby__pic');
      const links = section.querySelectorAll('.dr-shopby__list a');
      const show = (i) => {
        pics.forEach((p) => p.classList.toggle('is-active', p.dataset.index === i));
        links.forEach((l) => l.classList.toggle('is-active', l.dataset.index === i));
      };
      links.forEach((l) => {
        l.addEventListener('mouseenter', () => show(l.dataset.index));
        l.addEventListener('focus', () => show(l.dataset.index));
      });
    });
  };

  const init = (root) => { initAddForms(root); initRails(root); initCountdown(root); initShopBy(root); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
